import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { Linkedin, Mail } from "lucide-react";
import HelpCentreModal from "../../modals/HelpCentreModal";
import { useDisclosure } from "../../hooks/useDisclosure";

function LoggedInFooter() {
  return (
    <footer className="w-full border-t border-neutral-primary-soft">
      <div className="w-full px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-body/70 gap-2">
          <span>© 2026 Data Revival Ltd</span>
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2">
            <Link
              to="/legal/privacy-policy"
              className="hover:text-body transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms-of-service"
              className="hover:text-body transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/legal/cookie-policy"
              className="hover:text-body transition-colors"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function LoggedOutFooter({ onHelpClick }: { onHelpClick: () => void }) {
  return (
    <footer className="w-full bg-dark-bg border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* logo and description */}
          <div className="md:col-span-1">
            <a
              href="/"
              className="flex items-center mb-4 space-x-3 rtl:space-x-reverse"
            >
              <img
                src="/DataRevival_REV_RGB.svg"
                className="h-8"
                alt="DataRevival Logo"
              />
            </a>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Unlock hidden value in your chemical data with AI-powered data
              structuring and retrieval.
            </p>
          </div>

          {/* quick Links */}
          <div className="md:col-span-1">
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <Link
                  to="/howItWorks"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  How it works
                </Link>
              </li>
              <li>
                <Link
                  to="/pricingPlans"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  About us
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* legal */}
          <div className="md:col-span-1">
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <Link
                  to="/legal/privacy-policy"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/terms-of-service"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/terms-of-service"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Intellectual Property Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/cookie-policy"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* contact */}
          <div className="md:col-span-1">
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <button
                  onClick={onHelpClick}
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Help centre
                </button>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="hover:text-[#00A3FF] transition-colors duration-200"
                >
                  Contact us
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-sm text-gray-400">
              © 2026{" "}
              <a
                href="/"
                className="hover:text-[#00A3FF] transition-colors duration-200"
              >
                Data Revival Ltd
              </a>
              . All Rights Reserved.
            </span>

            <div className="flex gap-4">
              <a
                href="https://www.linkedin.com/company/data-revival-ltd/"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 hover:border-[#00A3FF]/50 transition-all duration-200"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={20} />
              </a>

              <a
                href="mailto:support@datarevival.com"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 hover:border-[#00A3FF]/50 transition-all duration-200"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Footer() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const helpModal = useDisclosure();
  const isLoggedIn = !!user;
  const isLandingPage = location.pathname === "/";
  const isTermsofService = location.pathname === "/legal/terms-of-service";
  const isPrivacyPolicy = location.pathname === "/legal/privacy-policy";
  const isCookiePolicy = location.pathname === "/legal/cookie-policy";
  const handleContactClick = () => navigate("/contact");

  const isPricingPage = location.pathname === "/pricingPlans";
  const isContactPage = location.pathname === "/contact";
  const isBlogPage = location.pathname.startsWith("/blog");
  const isHowItWorks = location.pathname === "/howItWorks";

  // loggedOutFooter on landing, legal, pricing, about us, blog, and contact pages regardless of auth status
  if (
    isLandingPage ||
    isPrivacyPolicy ||
    isTermsofService ||
    isCookiePolicy ||
    isPricingPage ||
    isContactPage ||
    isBlogPage ||
    isHowItWorks
  ) {
    return (
      <>
        <LoggedOutFooter onHelpClick={helpModal.open} />
        <HelpCentreModal
          isOpen={helpModal.isOpen}
          setIsOpen={helpModal.setIsOpen}
          onContactClick={handleContactClick}
        />
      </>
    );
  }

  return isLoggedIn ? (
    <LoggedInFooter />
  ) : (
    <>
      <LoggedOutFooter onHelpClick={helpModal.open} />
      <HelpCentreModal
        isOpen={helpModal.isOpen}
        setIsOpen={helpModal.setIsOpen}
        onContactClick={handleContactClick}
      />
    </>
  );
}

export default Footer;
